import { clsx } from "clsx";
import { Camera, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { getCroppedImage } from "@/utils/cropImage";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { Spinner } from "./Spinner";

export interface AvatarUploadProps {
  currentAvatarUrl: string | null;
  initials: string;
  onUpload: (file: Blob) => void;
  onDelete?: () => void;
  isUploading?: boolean;
}

export function AvatarUpload({
  currentAvatarUrl,
  initials,
  onUpload,
  onDelete,
  isUploading = false,
}: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be selected again
    e.target.value = "";
  }

  async function handleSaveCrop() {
    if (!imageSrc || !croppedAreaPixels) {
      return;
    }

    setIsCropping(true);
    try {
      const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels);
      onUpload(croppedBlob);
      closeCropModal();
    } finally {
      setIsCropping(false);
    }
  }

  function closeCropModal() {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }

  return (
    <div>
      {/* Avatar circle */}
      <div className="group relative h-24 w-24 shrink-0">
        <div
          className={clsx(
            "flex h-full w-full items-center justify-center overflow-hidden rounded-full",
            !currentAvatarUrl && "bg-adara-700",
          )}
        >
          {currentAvatarUrl ? (
            <img
              src={currentAvatarUrl}
              alt="Avatar"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-2xl font-semibold text-white">
              {initials}
            </span>
          )}
        </div>

        {/* Loading overlay */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
            <Spinner size="sm" className="text-white" />
          </div>
        )}

        {/* Hover overlay with actions */}
        {!isUploading && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-full bg-black/0 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => {
                fileInputRef.current?.click();
              }}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-panel/20 transition-colors hover:bg-panel/40"
              aria-label="Zmień avatar"
            >
              <Camera size={18} className="text-white" aria-hidden="true" />
            </button>
            {currentAvatarUrl && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-panel/20 transition-colors hover:bg-red-500/80"
                aria-label="Usuń avatar"
              >
                <Trash2 size={18} className="text-white" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Wybierz zdjęcie"
      />

      {/* Crop modal */}
      <Modal
        isOpen={imageSrc !== null}
        onClose={closeCropModal}
        title="Przytnij zdjęcie"
      >
        {imageSrc && (
          <>
            <div className="relative h-72 w-full overflow-hidden rounded-lg bg-gray-900">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom slider */}
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-dim">Powiększenie</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => {
                  setZoom(Number(e.target.value));
                }}
                className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-wash accent-adara-700"
                aria-label="Powiększenie"
              />
            </div>

            {/* Actions */}
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="outline" onClick={closeCropModal}>
                Anuluj
              </Button>
              <Button
                onClick={() => {
                  void handleSaveCrop();
                }}
                isLoading={isCropping}
              >
                Zapisz
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
